import React, { useState, useEffect } from "react";
import {
  CreditCard,
  Copy,
  Check,
  ExternalLink,
  Unlink,
  Link2,
  X,
  QrCode,
} from "lucide-react";
import {
  getMyCards,
  getProfiles,
  relinkCard,
  unlinkCardUser,
} from "../services/api";
import { useTranslation } from "../i18n/LanguageContext";

export default function CardsPage() {
  const { t } = useTranslation();
  const [cards, setCards] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [linkModal, setLinkModal] = useState(null);

  useEffect(() => {
    Promise.all([getMyCards(), getProfiles()])
      .then(([cardsRes, profilesRes]) => {
        setCards(cardsRes.data.cards);
        setProfiles(profilesRes.data.profiles);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyUrl = (code) => {
    const url = `${window.location.origin}/c/${code}`;
    navigator.clipboard.writeText(url);
    setCopiedId(code);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUnlink = async (code) => {
    try {
      const res = await unlinkCardUser(code);
      setCards((prev) =>
        prev.map((c) => (c.code === code ? res.data.card : c))
      );
    } catch (err) {
      // ignore
    }
  };

  const handleRelink = async (code, profileId) => {
    try {
      const res = await relinkCard(code, profileId);
      setCards((prev) =>
        prev.map((c) => (c.code === code ? res.data.card : c))
      );
      setLinkModal(null);
    } catch (err) {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("cards.title")}</h1>
        <p className="text-sm text-gray-500 mt-1">
          {t("cards.subtitle", { count: cards.length })}
        </p>
      </div>

      {cards.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <CreditCard className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t("cards.noCards")}</p>
          <p className="text-gray-400 text-xs mt-1">{t("cards.noCardsDesc")}</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {cards.map((card) => (
            <div
              key={card.id}
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5"
            >
              {/* Header: code + actions */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-gray-400" />
                  <code className="text-sm font-mono font-semibold text-gray-800">
                    {card.code}
                  </code>
                  <button
                    onClick={() => copyUrl(card.code)}
                    className="text-gray-300 hover:text-gray-600 transition"
                    title={t("cards.copyUrl")}
                  >
                    {copiedId === card.code ? (
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <QrCode className="h-3.5 w-3.5" />
                  {card.scan_count} {t("cards.scans")}
                  {card.unique_scan_count > 0 && (
                    <span className="text-indigo-500">({card.unique_scan_count} {t("cards.unique")})</span>
                  )}
                </div>
              </div>


              {/* Linked profile */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="text-sm">
                  {card.profile ? (
                    <div className="flex items-center gap-2">
                      {card.profile.profile_image ? (
                        <img
                          src={card.profile.profile_image}
                          alt=""
                          className="h-7 w-7 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {card.profile.profile_name?.charAt(0)?.toUpperCase() ||
                            "?"}
                        </div>
                      )}
                      <span className="font-medium text-gray-800">
                        {card.profile.profile_name}
                      </span>
                      <a
                        href={`/p/${card.profile.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-500 hover:text-indigo-600"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  ) : (
                    <span className="text-gray-400">{t("cards.notLinked")}</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {card.profile ? (
                    <>
                      <button
                        onClick={() => setLinkModal(card)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        {t("cards.change")}
                      </button>
                      <button
                        onClick={() => handleUnlink(card.code)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition"
                      >
                        <Unlink className="h-3.5 w-3.5" />
                        {t("cards.unlink")}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setLinkModal(card)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      {t("cards.linkProfile")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Link/Relink Modal */}
      {linkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setLinkModal(null)}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {t("cards.selectProfile")}
              </h3>
              <button
                onClick={() => setLinkModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {t("cards.linkCardTo", { code: linkModal.code })}
            </p>
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200">
              {profiles.map((profile) => (
                <button
                  key={profile.id}
                  onClick={() => handleRelink(linkModal.code, profile.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition text-left ${
                    linkModal.profile_id === profile.id
                      ? "bg-indigo-50"
                      : ""
                  }`}
                >
                  {profile.profile_image ? (
                    <img
                      src={profile.profile_image}
                      alt=""
                      className="h-9 w-9 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                      {profile.profile_name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {profile.profile_name}
                    </p>
                    <p className="text-xs text-gray-400">{profile.slug}</p>
                  </div>
                  {linkModal.profile_id === profile.id && (
                    <Check className="h-4 w-4 text-indigo-600" />
                  )}
                </button>
              ))}
              {profiles.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-gray-400">
                  {t("cards.noProfiles")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
